import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { ReactComponent as LogoDark } from '../../Logo-dark-mode.svg';
import { ReactComponent as LogoLight } from '../../Logo-light-mode.svg';

import './AppHeader.css';

/** Read theme from DOM (single source of truth for visual theme). */
function getThemeFromDocument() {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export const AppHeader = ({
  onThemeToggle,
  onImportConfigs,
  onExport,
  onNewClick,
  showNewButton = false,
  isNewModalOpen = false,
  currentTheme: currentThemeProp,
  isThemeSwitching: isThemeSwitchingProp,
  /** 'apca' | 'wcag' — contrast metric for contrast-based colors */
  contrastAlgorithm = 'apca',
  onContrastAlgorithmToggle,
  /** 'srgb' | 'p3' — workspace color space (affects APCA measurement; WCAG stays sRGB per spec) */
  colorSpace = 'srgb',
  onColorSpaceToggle,
  hideImportExport = false,
  logoHref = '',
  onLogoClick,
  className = '',
  ...props
}) => {
  // When parent does not control theme (no currentTheme passed), header owns theme state
  // so that theme toggle does not re-render the whole app (e.g. hundreds of color cards).
  const isControlled = currentThemeProp !== undefined;
  const [localTheme, setLocalTheme] = useState(getThemeFromDocument);
  const [localThemeSwitching, setLocalThemeSwitching] = useState(false);
  const theme = isControlled ? currentThemeProp : localTheme;
  const isThemeSwitching = isControlled ? (isThemeSwitchingProp ?? false) : localThemeSwitching;
  const onThemeToggleRef = useRef(onThemeToggle);
  onThemeToggleRef.current = onThemeToggle;

  // Sync local theme with document when document changes externally (e.g. restore)
  useEffect(() => {
    if (isControlled) return;
    const check = () => setLocalTheme(getThemeFromDocument());
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [isControlled]);

  const handleThemeToggle = () => {
    const current = getThemeFromDocument();
    const next = current === 'dark' ? 'light' : 'dark';
    const root = document.documentElement;
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (isControlled && onThemeToggle) {
      // Parent controls theme (e.g. Playground)
      onThemeToggle();
      return;
    }

    if (!isControlled) setLocalThemeSwitching(true);
    if (!prefersReduced) {
      root.classList.add('theme-animating');
    }
    root.setAttribute('data-theme', next);
    if (!prefersReduced) {
      setTimeout(() => {
        root.classList.remove('theme-animating');
        if (!isControlled) setLocalThemeSwitching(false);
      }, 180);
    } else if (!isControlled) {
      setLocalThemeSwitching(false);
    }
    if (!isControlled) setLocalTheme(next);
    onThemeToggleRef.current?.();
  };

  const baseClass = 'app-header';
  const combinedClassName = [baseClass, className].filter(Boolean).join(' ');

  const logoContent = (
    <>
      {theme === 'dark' ? (
        <LogoDark className="app-header__logo-svg" />
      ) : (
        <LogoLight className="app-header__logo-svg" />
      )}
      <span className="app-header__version">v1.3</span>
    </>
  );

  return (
    <header className={combinedClassName} {...props}>
      <div className="app-header__content">
        {/* Logo */}
        {logoHref ? (
          <a
            href={logoHref}
            target="_blank"
            rel="noopener noreferrer"
            className="app-header__logo app-header__logo--link"
            onClick={onLogoClick ? (e) => { e.preventDefault(); onLogoClick(); } : undefined}
            aria-label="Go to home"
          >
            {logoContent}
          </a>
        ) : (
          <div className="app-header__logo">
            {logoContent}
          </div>
        )}

        {/* Actions */}
        <div className="app-header__actions">
          {onContrastAlgorithmToggle && (
            <Button
              type="ghost"
              size="medium"
              onClick={onContrastAlgorithmToggle}
              className="app-header__contrast-toggle tooltip"
              data-tooltip="Current contrast algorithm"
              data-tooltip-position="bottom"
              aria-label="Current contrast algorithm"
            >
              {contrastAlgorithm === 'wcag' ? 'WCAG' : 'APCA'}
            </Button>
          )}
          {onColorSpaceToggle && (
            <Button
              type="ghost"
              size="medium"
              onClick={onColorSpaceToggle}
              className="app-header__color-space-toggle tooltip"
              data-tooltip={
                colorSpace === 'p3'
                  ? 'Workspace color space: Display P3'
                  : 'Workspace color space: sRGB'
              }
              data-tooltip-position="bottom"
              aria-label="Current workspace color space"
            >
              {colorSpace === 'p3' ? 'P3' : 'sRGB'}
            </Button>
          )}
          {/* App mode switcher */}
          <Button
            type="ghost"
            size="medium"
            icon={<Icon name={theme === 'dark' ? 'light-mode' : 'dark-mode'} size="medium" />}
            onClick={handleThemeToggle}
            disabled={isThemeSwitching}
            className="app-header__theme-toggle tooltip"
            data-tooltip={theme === 'dark' ? 'Light theme' : 'Dark theme'}
            data-tooltip-position="bottom"
          />
          {/* Help */}
          <Button
                type="outline"
                size="medium"
                icon={<Icon name="help" size="medium" />}
                onClick={() => window.open('https://www.notion.so/solo-cube/309881b3372180f2a855c4ea77fca1c7?v=309881b3372180778d84000c800841c2&source=copy_link', '_blank')}
                className="app-header__help-button tooltip"
                data-tooltip="Help"
                data-tooltip-position="bottom"
                aria-label="Help"
              />
          {showNewButton && onNewClick && (
            <Button
              type={isNewModalOpen ? "solid" : "outline"}
              size="medium"
              icon={<Icon name={isNewModalOpen ? "close" : "add"} size="medium" />}
              onClick={onNewClick}
              
            >
              New
            </Button>
          )}
          {!hideImportExport && (
            <>
              {/* Export button */}
              <Button
                type="solid"
                size="medium"
                icon={<Icon name="download" size="medium" />}
                onClick={onExport}
              >
                Export
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
